import React, { useEffect, useState, useRef } from 'react';
import { LoggedInNav } from '../components/LoggedInNav';
import { XIcon, SaveIcon, EditIcon, PlusIcon, TrashIcon, MoveIcon, TypeIcon, ArrowUpRightIcon } from 'lucide-react';
type Box = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  title: string;
  subtitle?: string;
  bulletPoints: string[];
  footnote?: string;
  category: 'sources' | 'processing' | 'intelligence' | 'persona' | 'delivery' | 'edge';
};
type Arrow = {
  id: string;
  type: 'curved' | 'straight' | 'elbow';
  sourceId: string;
  targetId: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  controlPoints?: {
    x: number;
    y: number;
  }[];
};
type TextLabel = {
  id: string;
  x: number;
  y: number;
  text: string;
  fontSize: number;
  fontWeight: number;
  category: 'title' | 'subtitle' | 'label';
};
export function Design() {
  // State for diagram elements
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [arrows, setArrows] = useState<Arrow[]>([]);
  const [labels, setLabels] = useState<TextLabel[]>([]);
  // Editing state
  const [editMode, setEditMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState<{
    type: 'box' | 'arrow' | 'label';
    id: string;
  } | null>(null);
  const [editingText, setEditingText] = useState<{
    id: string;
    field: string;
    text: string;
  } | null>(null);
  const [hoveredElement, setHoveredElement] = useState<{
    type: 'box' | 'arrow' | 'label';
    id: string;
  } | null>(null);
  // Drag state
  const [draggedElement, setDraggedElement] = useState<{
    type: 'box' | 'arrow' | 'label';
    id: string;
    startX: number;
    startY: number;
  } | null>(null);
  const [dragOffset, setDragOffset] = useState({
    x: 0,
    y: 0
  });
  // Refs
  const svgRef = useRef<SVGSVGElement>(null);
  const diagramContainerRef = useRef<HTMLDivElement>(null);
  // Load initial diagram data
  useEffect(() => {
    // Initialize with the existing diagram structure
    setBoxes([
    // Sources column
    {
      id: 'calendar',
      x: 60,
      y: 90,
      width: 260,
      height: 120,
      title: 'Calendars',
      bulletPoints: ['Google Calendar', 'Microsoft Outlook'],
      footnote: 'Primary trigger: upcoming meetings',
      category: 'sources'
    }, {
      id: 'email',
      x: 60,
      y: 240,
      width: 260,
      height: 120,
      title: 'Email',
      bulletPoints: ['Gmail', 'Microsoft 365 / Exchange'],
      footnote: 'Thread scans per attendee',
      category: 'sources'
    }, {
      id: 'crm',
      x: 60,
      y: 390,
      width: 260,
      height: 140,
      title: 'CRM & Data',
      bulletPoints: ['Salesforce / HubSpot', 'Pipeline, cases, accounts', 'Custom objects, notes'],
      footnote: 'Enrichment and status',
      category: 'sources'
    }, {
      id: 'knowledge',
      x: 60,
      y: 560,
      width: 260,
      height: 160,
      title: 'Knowledge / Files',
      bulletPoints: ['Google Drive / SharePoint', 'Notion / Confluence', 'Past briefs, proposals'],
      footnote: 'Optional RAG context',
      category: 'sources'
    }, {
      id: 'identity',
      x: 60,
      y: 740,
      width: 260,
      height: 120,
      title: 'Identity & Directory',
      bulletPoints: ['Company Graph (domain map)', 'HRIS / Org chart (opt.)'],
      category: 'sources'
    },
    // Processing Pipeline
    {
      id: 'scheduler',
      x: 420,
      y: 90,
      width: 300,
      height: 80,
      title: 'Orchestrator & Scheduler',
      bulletPoints: [],
      footnote: 'Runs per upcoming meeting window',
      category: 'processing'
    }, {
      id: 'resolver',
      x: 420,
      y: 190,
      width: 300,
      height: 90,
      title: 'Attendee & Entity Resolver',
      bulletPoints: [],
      footnote: 'Emails → Contacts/Accounts mapping',
      category: 'processing'
    }, {
      id: 'inbox',
      x: 420,
      y: 300,
      width: 300,
      height: 110,
      title: 'Contextual Inbox Scan',
      bulletPoints: [],
      footnote: 'Per‑attendee recent threads, attachments, commitments',
      category: 'processing'
    }, {
      id: 'crmquery',
      x: 420,
      y: 430,
      width: 300,
      height: 110,
      title: 'CRM & Case/Pipeline Lookup',
      bulletPoints: [],
      footnote: 'Open opps, ARR, stage changes, open cases, SLAs',
      category: 'processing'
    }, {
      id: 'knowledgepull',
      x: 420,
      y: 560,
      width: 300,
      height: 110,
      title: 'Knowledge Retrieval (RAG)',
      bulletPoints: [],
      footnote: 'Relevant docs, prior briefs, proposals, FAQs',
      category: 'processing'
    }, {
      id: 'compliance',
      x: 420,
      y: 690,
      width: 300,
      height: 100,
      title: 'Privacy, Security & Guardrails',
      bulletPoints: [],
      footnote: 'Scoped access, redaction, PII minimization, audit',
      category: 'processing'
    },
    // Intelligence Core
    {
      id: 'normalization',
      x: 840,
      y: 90,
      width: 300,
      height: 110,
      title: 'Unification & Normalization',
      bulletPoints: [],
      footnote: 'Dedup, thread stitching, scoring',
      category: 'intelligence'
    }, {
      id: 'contextbuilder',
      x: 840,
      y: 220,
      width: 300,
      height: 130,
      title: 'Context Graph Builder',
      bulletPoints: [],
      footnote: 'People ↔ Accounts ↔ Deals ↔ Cases ↔ Docs\nRisk/opportunity signals, blockers, action items',
      category: 'intelligence'
    }, {
      id: 'reasoning',
      x: 840,
      y: 370,
      width: 300,
      height: 120,
      title: 'Reasoning & Insight Engine',
      bulletPoints: [],
      footnote: 'LLM + rules + company playbooks',
      category: 'intelligence'
    }, {
      id: 'briefgen',
      x: 840,
      y: 510,
      width: 300,
      height: 120,
      title: 'Brief Generator',
      bulletPoints: [],
      footnote: "Agenda, who's who, context, talking points, risks",
      category: 'intelligence'
    }, {
      id: 'actionplanner',
      x: 840,
      y: 650,
      width: 300,
      height: 100,
      title: 'Action Planner',
      bulletPoints: [],
      footnote: 'Tasks, follow‑ups, next steps',
      category: 'intelligence'
    },
    // Persona Formatting
    {
      id: 'renderer',
      x: 1200,
      y: 90,
      width: 320,
      height: 170,
      title: 'Persona‑Based Renderer',
      bulletPoints: [],
      footnote: 'Sales • Customer Service • Executive\nTailors KPIs, tone, highlights, CTAs',
      category: 'persona'
    }, {
      id: 'templates',
      x: 1200,
      y: 280,
      width: 320,
      height: 120,
      title: 'Templates & Playbooks',
      bulletPoints: [],
      footnote: 'Company‑specific formatting & guardrails',
      category: 'persona'
    }, {
      id: 'localization',
      x: 1200,
      y: 420,
      width: 320,
      height: 120,
      title: 'Localization & Policies',
      bulletPoints: [],
      footnote: 'Language, region, compliance',
      category: 'persona'
    }, {
      id: 'delivery',
      x: 1200,
      y: 570,
      width: 320,
      height: 120,
      title: 'Delivery Channels',
      bulletPoints: [],
      footnote: 'Slack • Email (Inbox) • Microsoft Teams',
      category: 'delivery'
    }, {
      id: 'feedback',
      x: 1200,
      y: 710,
      width: 320,
      height: 120,
      title: 'Feedback & Analytics',
      bulletPoints: [],
      footnote: 'Rating, edits, engagement, auto‑learning',
      category: 'delivery'
    },
    // Edge callout
    {
      id: 'runtime',
      x: 60,
      y: 900,
      width: 1460,
      height: 140,
      title: 'Run‑Time Flow (High Level)',
      bulletPoints: [],
      footnote: '1) Calendar trigger picks upcoming meetings → 2) Resolve attendees and identities → 3) Scan recent email threads & files → 4) Query CRM for pipeline/case context → 5) Build context graph & reason → 6) Generate persona‑based brief → 7) Deliver to Slack/Inbox/Teams → 8) Capture feedback to improve future briefs.\n\nCross‑cutting: Security, consent, audit logging, PII minimization, rate‑limit orchestration, caching.',
      category: 'edge'
    }]);
    // Initialize text labels
    setLabels([{
      id: 'sources-title',
      x: 100,
      y: 50,
      text: 'Sources',
      fontSize: 20,
      fontWeight: 700,
      category: 'title'
    }, {
      id: 'processing-title',
      x: 520,
      y: 50,
      text: 'Processing Pipeline',
      fontSize: 20,
      fontWeight: 700,
      category: 'title'
    }, {
      id: 'intelligence-title',
      x: 940,
      y: 50,
      text: 'Intelligence Core',
      fontSize: 20,
      fontWeight: 700,
      category: 'title'
    }, {
      id: 'persona-title',
      x: 1240,
      y: 50,
      text: 'Persona Formatting',
      fontSize: 20,
      fontWeight: 700,
      category: 'title'
    }]);
    // Initialize arrows
    setArrows([
    // Sources to Pipeline
    {
      id: 'arrow-calendar-scheduler',
      type: 'curved',
      sourceId: 'calendar',
      targetId: 'scheduler',
      sourceX: 320,
      sourceY: 150,
      targetX: 420,
      targetY: 130,
      controlPoints: [{
        x: 360,
        y: 150
      }, {
        x: 380,
        y: 130
      }]
    }, {
      id: 'arrow-email-resolver',
      type: 'curved',
      sourceId: 'email',
      targetId: 'resolver',
      sourceX: 320,
      sourceY: 300,
      targetX: 420,
      targetY: 220,
      controlPoints: [{
        x: 360,
        y: 300
      }, {
        x: 380,
        y: 220
      }]
    }, {
      id: 'arrow-crm-inbox',
      type: 'curved',
      sourceId: 'crm',
      targetId: 'inbox',
      sourceX: 320,
      sourceY: 460,
      targetX: 420,
      targetY: 330,
      controlPoints: [{
        x: 360,
        y: 460
      }, {
        x: 380,
        y: 330
      }]
    }, {
      id: 'arrow-knowledge-crmquery',
      type: 'curved',
      sourceId: 'knowledge',
      targetId: 'crmquery',
      sourceX: 320,
      sourceY: 640,
      targetX: 420,
      targetY: 470,
      controlPoints: [{
        x: 360,
        y: 640
      }, {
        x: 380,
        y: 450
      }]
    }, {
      id: 'arrow-identity-compliance',
      type: 'curved',
      sourceId: 'identity',
      targetId: 'compliance',
      sourceX: 320,
      sourceY: 800,
      targetX: 420,
      targetY: 735,
      controlPoints: [{
        x: 360,
        y: 800
      }, {
        x: 380,
        y: 705
      }]
    },
    // Pipeline vertical arrows
    {
      id: 'arrow-scheduler-resolver',
      type: 'straight',
      sourceId: 'scheduler',
      targetId: 'resolver',
      sourceX: 570,
      sourceY: 170,
      targetX: 570,
      targetY: 190,
      controlPoints: []
    }, {
      id: 'arrow-resolver-inbox',
      type: 'straight',
      sourceId: 'resolver',
      targetId: 'inbox',
      sourceX: 570,
      sourceY: 280,
      targetX: 570,
      targetY: 300,
      controlPoints: []
    }, {
      id: 'arrow-inbox-crmquery',
      type: 'straight',
      sourceId: 'inbox',
      targetId: 'crmquery',
      sourceX: 570,
      sourceY: 410,
      targetX: 570,
      targetY: 430,
      controlPoints: []
    }, {
      id: 'arrow-crmquery-knowledgepull',
      type: 'straight',
      sourceId: 'crmquery',
      targetId: 'knowledgepull',
      sourceX: 570,
      sourceY: 540,
      targetX: 570,
      targetY: 560,
      controlPoints: []
    },
    // Pipeline to Intelligence
    {
      id: 'arrow-resolver-normalization',
      type: 'curved',
      sourceId: 'resolver',
      targetId: 'normalization',
      sourceX: 720,
      sourceY: 240,
      targetX: 840,
      targetY: 145,
      controlPoints: [{
        x: 770,
        y: 240
      }, {
        x: 790,
        y: 145
      }]
    }, {
      id: 'arrow-inbox-contextbuilder',
      type: 'curved',
      sourceId: 'inbox',
      targetId: 'contextbuilder',
      sourceX: 720,
      sourceY: 360,
      targetX: 840,
      targetY: 285,
      controlPoints: [{
        x: 770,
        y: 360
      }, {
        x: 790,
        y: 285
      }]
    }, {
      id: 'arrow-crmquery-reasoning',
      type: 'curved',
      sourceId: 'crmquery',
      targetId: 'reasoning',
      sourceX: 720,
      sourceY: 490,
      targetX: 840,
      targetY: 430,
      controlPoints: [{
        x: 770,
        y: 490
      }, {
        x: 790,
        y: 430
      }]
    }, {
      id: 'arrow-knowledgepull-briefgen',
      type: 'curved',
      sourceId: 'knowledgepull',
      targetId: 'briefgen',
      sourceX: 720,
      sourceY: 615,
      targetX: 840,
      targetY: 570,
      controlPoints: [{
        x: 770,
        y: 615
      }, {
        x: 790,
        y: 570
      }]
    }, {
      id: 'arrow-compliance-actionplanner',
      type: 'curved',
      sourceId: 'compliance',
      targetId: 'actionplanner',
      sourceX: 720,
      sourceY: 740,
      targetX: 840,
      targetY: 695,
      controlPoints: [{
        x: 770,
        y: 740
      }, {
        x: 790,
        y: 695
      }]
    },
    // Intelligence vertical arrows
    {
      id: 'arrow-normalization-contextbuilder',
      type: 'straight',
      sourceId: 'normalization',
      targetId: 'contextbuilder',
      sourceX: 990,
      sourceY: 200,
      targetX: 990,
      targetY: 220,
      controlPoints: []
    }, {
      id: 'arrow-contextbuilder-reasoning',
      type: 'straight',
      sourceId: 'contextbuilder',
      targetId: 'reasoning',
      sourceX: 990,
      sourceY: 350,
      targetX: 990,
      targetY: 370,
      controlPoints: []
    }, {
      id: 'arrow-reasoning-briefgen',
      type: 'straight',
      sourceId: 'reasoning',
      targetId: 'briefgen',
      sourceX: 990,
      sourceY: 490,
      targetX: 990,
      targetY: 510,
      controlPoints: []
    },
    // Intelligence to Persona
    {
      id: 'arrow-contextbuilder-renderer',
      type: 'curved',
      sourceId: 'contextbuilder',
      targetId: 'renderer',
      sourceX: 1140,
      sourceY: 275,
      targetX: 1200,
      targetY: 215,
      controlPoints: [{
        x: 1170,
        y: 275
      }, {
        x: 1180,
        y: 215
      }]
    }, {
      id: 'arrow-briefgen-templates',
      type: 'curved',
      sourceId: 'briefgen',
      targetId: 'templates',
      sourceX: 1140,
      sourceY: 570,
      targetX: 1200,
      targetY: 340,
      controlPoints: [{
        x: 1170,
        y: 570
      }, {
        x: 1180,
        y: 340
      }]
    }, {
      id: 'arrow-actionplanner-localization',
      type: 'curved',
      sourceId: 'actionplanner',
      targetId: 'localization',
      sourceX: 1140,
      sourceY: 705,
      targetX: 1200,
      targetY: 460,
      controlPoints: [{
        x: 1170,
        y: 705
      }, {
        x: 1180,
        y: 460
      }]
    },
    // Persona to Delivery
    {
      id: 'arrow-localization-delivery',
      type: 'straight',
      sourceId: 'localization',
      targetId: 'delivery',
      sourceX: 1360,
      sourceY: 500,
      targetX: 1360,
      targetY: 570,
      controlPoints: []
    },
    // Feedback loop
    {
      id: 'arrow-feedback-actionplanner',
      type: 'curved',
      sourceId: 'feedback',
      targetId: 'actionplanner',
      sourceX: 1200,
      sourceY: 770,
      targetX: 840,
      targetY: 760,
      controlPoints: [{
        x: 1100,
        y: 880
      }, {
        x: 950,
        y: 860
      }]
    }]);
  }, []);
  // Convert SVG coordinates to screen coordinates
  const svgToScreenCoordinates = (x: number, y: number) => {
    if (!svgRef.current) return {
      x,
      y
    };
    const svgRect = svgRef.current.getBoundingClientRect();
    const svgPoint = svgRef.current.createSVGPoint();
    svgPoint.x = x;
    svgPoint.y = y;
    const screenPoint = svgPoint.matrixTransform(svgRef.current.getScreenCTM()!);
    return {
      x: screenPoint.x,
      y: screenPoint.y
    };
  };
  // Convert screen coordinates to SVG coordinates
  const screenToSvgCoordinates = (x: number, y: number) => {
    if (!svgRef.current) return {
      x,
      y
    };
    const svgRect = svgRef.current.getBoundingClientRect();
    const svgPoint = svgRef.current.createSVGPoint();
    svgPoint.x = x;
    svgPoint.y = y;
    const svgScreenCTM = svgRef.current.getScreenCTM();
    if (!svgScreenCTM) return {
      x,
      y
    };
    const inverseScreenCTM = svgScreenCTM.inverse();
    const svgCoords = svgPoint.matrixTransform(inverseScreenCTM);
    return {
      x: svgCoords.x,
      y: svgCoords.y
    };
  };
  // Handle mouse down for dragging
  const handleMouseDown = (e: React.MouseEvent, type: 'box' | 'arrow' | 'label', id: string) => {
    if (!editMode) return;
    e.preventDefault();
    const {
      clientX,
      clientY
    } = e;
    setSelectedElement({
      type,
      id
    });
    // Calculate drag offset
    let offsetX = 0;
    let offsetY = 0;
    if (type === 'box') {
      const box = boxes.find(b => b.id === id);
      if (box) {
        const screenCoords = svgToScreenCoordinates(box.x, box.y);
        offsetX = clientX - screenCoords.x;
        offsetY = clientY - screenCoords.y;
      }
    } else if (type === 'label') {
      const label = labels.find(l => l.id === id);
      if (label) {
        const screenCoords = svgToScreenCoordinates(label.x, label.y);
        offsetX = clientX - screenCoords.x;
        offsetY = clientY - screenCoords.y;
      }
    }
    setDragOffset({
      x: offsetX,
      y: offsetY
    });
    setDraggedElement({
      type,
      id,
      startX: clientX,
      startY: clientY
    });
  };
  // Handle mouse move for dragging
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!editMode || !draggedElement) return;
    e.preventDefault();
    const {
      clientX,
      clientY
    } = e;
    const svgCoords = screenToSvgCoordinates(clientX - dragOffset.x, clientY - dragOffset.y);
    if (draggedElement.type === 'box') {
      setBoxes(boxes.map(box => {
        if (box.id === draggedElement.id) {
          const newBox = {
            ...box,
            x: svgCoords.x,
            y: svgCoords.y
          };
          // Update connected arrows
          setArrows(arrows.map(arrow => {
            if (arrow.sourceId === box.id) {
              const dx = newBox.x - box.x;
              const dy = newBox.y - box.y;
              return {
                ...arrow,
                sourceX: arrow.sourceX + dx,
                sourceY: arrow.sourceY + dy,
                controlPoints: arrow.controlPoints?.map(pt => ({
                  x: pt.x + (pt === arrow.controlPoints?.[0] ? dx : 0),
                  y: pt.y + (pt === arrow.controlPoints?.[0] ? dy : 0)
                }))
              };
            }
            if (arrow.targetId === box.id) {
              const dx = newBox.x - box.x;
              const dy = newBox.y - box.y;
              return {
                ...arrow,
                targetX: arrow.targetX + dx,
                targetY: arrow.targetY + dy,
                controlPoints: arrow.controlPoints?.map(pt => ({
                  x: pt.x + (pt === arrow.controlPoints?.[arrow.controlPoints.length - 1] ? dx : 0),
                  y: pt.y + (pt === arrow.controlPoints?.[arrow.controlPoints.length - 1] ? dy : 0)
                }))
              };
            }
            return arrow;
          }));
          return newBox;
        }
        return box;
      }));
    } else if (draggedElement.type === 'label') {
      setLabels(labels.map(label => {
        if (label.id === draggedElement.id) {
          return {
            ...label,
            x: svgCoords.x,
            y: svgCoords.y
          };
        }
        return label;
      }));
    } else if (draggedElement.type === 'arrow') {
      // Handle arrow control point dragging
      // This would be more complex and require identifying which control point is being dragged
    }
  };
  // Handle mouse up for dragging
  const handleMouseUp = () => {
    setDraggedElement(null);
  };
  // Handle text edit click
  const handleTextClick = (e: React.MouseEvent, elementType: 'box' | 'label', id: string, field: string, text: string) => {
    if (!editMode) return;
    e.stopPropagation();
    setEditingText({
      id,
      field,
      text
    });
  };
  // Handle text edit change
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (!editingText) return;
    setEditingText({
      ...editingText,
      text: e.target.value
    });
  };
  // Handle text edit save
  const handleTextSave = () => {
    if (!editingText) return;
    const {
      id,
      field,
      text
    } = editingText;
    if (field === 'title' || field === 'footnote' || field === 'bulletPoints') {
      setBoxes(boxes.map(box => {
        if (box.id === id) {
          if (field === 'bulletPoints') {
            return {
              ...box,
              bulletPoints: text.split('\n')
            };
          }
          return {
            ...box,
            [field]: text
          };
        }
        return box;
      }));
    } else if (field === 'text') {
      setLabels(labels.map(label => {
        if (label.id === id) {
          return {
            ...box,
            text
          };
        }
        return label;
      }));
    }
    setEditingText(null);
  };
  // Generate path for arrows
  const generateArrowPath = (arrow: Arrow) => {
    if (arrow.type === 'straight') {
      return `M${arrow.sourceX} ${arrow.sourceY} L${arrow.targetX} ${arrow.targetY}`;
    } else if (arrow.type === 'curved') {
      if (arrow.controlPoints && arrow.controlPoints.length >= 2) {
        const cp1 = arrow.controlPoints[0];
        const cp2 = arrow.controlPoints[1];
        return `M${arrow.sourceX} ${arrow.sourceY} C${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${arrow.targetX} ${arrow.targetY}`;
      }
      // Default curve if control points aren't specified
      return `M${arrow.sourceX} ${arrow.sourceY} C${arrow.sourceX + 50} ${arrow.sourceY}, ${arrow.targetX - 50} ${arrow.targetY}, ${arrow.targetX} ${arrow.targetY}`;
    }
    return '';
  };
  // Get box fill color based on category and hover state
  const getBoxFill = (boxId: string, category: string) => {
    const isHovered = hoveredElement?.type === 'box' && hoveredElement.id === boxId;
    const isSelected = selectedElement?.type === 'box' && selectedElement.id === boxId;
    if (isSelected) return '#C8F169';
    if (isHovered) return '#C8F169';
    return '#1A2331';
  };
  // Get text color based on box state
  const getTextColor = (boxId: string, defaultColor: string) => {
    const isHovered = hoveredElement?.type === 'box' && hoveredElement.id === boxId;
    const isSelected = selectedElement?.type === 'box' && selectedElement.id === boxId;
    if (isSelected || isHovered) return '#1E2A38';
    return defaultColor;
  };
  // Save diagram to local storage
  const saveDiagram = () => {
    const diagramData = {
      boxes,
      arrows,
      labels
    };
    localStorage.setItem('prepzSystemDiagram', JSON.stringify(diagramData));
    alert('Diagram saved successfully!');
  };
  // Load diagram from local storage
  const loadDiagram = () => {
    const savedDiagram = localStorage.getItem('prepzSystemDiagram');
    if (savedDiagram) {
      const diagramData = JSON.parse(savedDiagram);
      setBoxes(diagramData.boxes);
      setArrows(diagramData.arrows);
      setLabels(diagramData.labels);
    }
  };
  return <div className="w-full min-h-screen bg-[#1E2A38] text-[#F5F7FA] font-sans" data-id="element-1759">
      <LoggedInNav data-id="element-1760" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1761">
        <div className="flex justify-between items-center mb-4" data-id="element-1762">
          <div data-id="element-1763">
            <h1 className="text-3xl font-bold mb-2" data-id="element-1764">
              Prepz Intelligence Platform — System Overview
            </h1>
            <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-1765">
              Scans calendars → gathers email, CRM, and knowledge context per
              meeting → generates persona‑based briefs → delivers to
              Slack/Inbox/Teams → captures feedback to continuously improve.
            </p>
          </div>
          <div className="flex space-x-2" data-id="element-1766">
            <button onClick={() => setEditMode(!editMode)} className={`flex items-center px-3 py-2 rounded ${editMode ? 'bg-[#C8F169] text-[#1E2A38]' : 'bg-[#1A2331] text-[#F5F7FA]'}`} data-id="element-1767">
              <EditIcon size={16} className="mr-2" data-id="element-1768" />
              {editMode ? 'Exit Edit Mode' : 'Edit Diagram'}
            </button>
            {editMode && <>
                <button onClick={saveDiagram} className="flex items-center px-3 py-2 rounded bg-[#1A2331] text-[#F5F7FA] hover:bg-[#1A2331]/80" data-id="element-1769">
                  <SaveIcon size={16} className="mr-2" data-id="element-1770" />
                  Save
                </button>
                <button onClick={loadDiagram} className="flex items-center px-3 py-2 rounded bg-[#1A2331] text-[#F5F7FA] hover:bg-[#1A2331]/80" data-id="element-1771">
                  <ArrowUpRightIcon size={16} className="mr-2" data-id="element-1772" />
                  Load
                </button>
              </>}
          </div>
        </div>
        {editMode && <div className="mb-4 p-3 bg-[#1A2331] rounded-lg text-sm" data-id="element-1773">
            <p className="font-medium mb-2" data-id="element-1774">Edit Mode Instructions:</p>
            <ul className="list-disc pl-5 space-y-1" data-id="element-1775">
              <li data-id="element-1776">Click and drag boxes or labels to reposition them</li>
              <li data-id="element-1777">Click on text to edit content</li>
              <li data-id="element-1778">Connected arrows will move with boxes automatically</li>
              <li data-id="element-1779">Click Save to preserve your changes</li>
            </ul>
          </div>}
        <div ref={diagramContainerRef} className="w-full overflow-auto rounded-xl border border-[#F5F7FA] border-opacity-10 shadow-lg bg-[#141E2A]" onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} data-id="element-1780">
          <svg ref={svgRef} viewBox="0 0 1600 1100" className="w-full h-auto" onMouseMove={handleMouseMove} data-id="element-1781">
            {/* Defs */}
            <defs data-id="element-1782">
              <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%" data-id="element-1783">
                <feDropShadow dx="0" dy="2" stdDeviation="6" floodOpacity="0.3" data-id="element-1784" />
              </filter>
              <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse" data-id="element-1785">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#C8F169" data-id="element-1786" />
              </marker>
            </defs>
            {/* Lane titles */}
            <g data-id="element-1787">
              {labels.map(label => <text key={label.id} x={label.x} y={label.y} className="fill-[#F5F7FA]" style={{
              fontSize: label.fontSize,
              fontWeight: label.fontWeight,
              cursor: editMode ? 'move' : 'default'
            }} onMouseDown={e => handleMouseDown(e, 'label', label.id)} onClick={e => handleTextClick(e, 'label', label.id, 'text', label.text)} data-id="element-1788">
                  {label.text}
                </text>)}
            </g>
            {/* Boxes */}
            {boxes.map(box => <g key={box.id} filter="url(#shadow)" onMouseEnter={() => setHoveredElement({
            type: 'box',
            id: box.id
          })} onMouseLeave={() => setHoveredElement(null)} data-id="element-1789">
                <rect x={box.x} y={box.y} width={box.width} height={box.height} rx="18" fill={getBoxFill(box.id, box.category)} stroke="#F5F7FA" strokeOpacity="0.1" style={{
              cursor: editMode ? 'move' : 'pointer',
              transition: 'fill 0.3s ease'
            }} onMouseDown={e => handleMouseDown(e, 'box', box.id)} data-id="element-1790" />
                <text x={box.x + 20} y={box.y + 30} style={{
              fontSize: 16,
              fontWeight: 700,
              fill: getTextColor(box.id, '#F5F7FA'),
              cursor: editMode ? 'text' : 'default'
            }} onClick={e => handleTextClick(e, 'box', box.id, 'title', box.title)} data-id="element-1791">
                  {box.title}
                </text>
                {box.bulletPoints.map((point, idx) => <text key={`${box.id}-bullet-${idx}`} x={box.x + 20} y={box.y + 55 + idx * 23} style={{
              fontSize: 13,
              fill: getTextColor(box.id, '#F5F7FA'),
              cursor: editMode ? 'text' : 'default'
            }} onClick={e => handleTextClick(e, 'box', box.id, 'bulletPoints', box.bulletPoints.join('\n'))} data-id="element-1792">
                    {point && `- ${point}`}
                  </text>)}
                {box.footnote && <text x={box.x + 20} y={box.y + box.height - 20} style={{
              fontSize: 12,
              fill: getTextColor(box.id, '#F5F7FA'),
              opacity: 0.7,
              cursor: editMode ? 'text' : 'default'
            }} onClick={e => handleTextClick(e, 'box', box.id, 'footnote', box.footnote)} data-id="element-1793">
                    {box.footnote}
                  </text>}
              </g>)}
            {/* Arrows */}
            <g stroke="#C8F169" strokeWidth="2" fill="none" markerEnd="url(#arrow)" opacity="0.7" data-id="element-1794">
              {arrows.map(arrow => <path key={arrow.id} d={generateArrowPath(arrow)} onMouseEnter={() => setHoveredElement({
              type: 'arrow',
              id: arrow.id
            })} onMouseLeave={() => setHoveredElement(null)} style={{
              cursor: editMode ? 'pointer' : 'default'
            }} data-id="element-1795" />)}
            </g>
            {/* Legend */}
            <g data-id="element-1796">
              <rect x="1200" y="20" width="340" height="46" rx="12" fill="#1A2331" stroke="#F5F7FA" strokeOpacity="0.1" data-id="element-1797" />
              <circle cx="1230" cy="43" r="6" fill="#1A2331" stroke="#C8F169" strokeOpacity="0.5" data-id="element-1798" />
              <text x="1245" y="47" style={{
              fontSize: 12,
              fill: '#F5F7FA'
            }} data-id="element-1799">
                Processing
              </text>
              <circle cx="1330" cy="43" r="6" fill="#1A2331" stroke="#C8F169" strokeOpacity="0.7" data-id="element-1800" />
              <text x="1345" y="47" style={{
              fontSize: 12,
              fill: '#F5F7FA'
            }} data-id="element-1801">
                Intelligence
              </text>
              <circle cx="1435" cy="43" r="6" fill="#1A2331" stroke="#C8F169" strokeOpacity="0.9" data-id="element-1802" />
              <text x="1450" y="47" style={{
              fontSize: 12,
              fill: '#F5F7FA'
            }} data-id="element-1803">
                Persona/Format
              </text>
            </g>
          </svg>
        </div>
        {/* Editing Modal */}
        {editingText && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1804">
            <div className="bg-[#1E2A38] p-6 rounded-lg max-w-lg w-full" data-id="element-1805">
              <div className="flex justify-between items-center mb-4" data-id="element-1806">
                <h3 className="text-lg font-medium" data-id="element-1807">Edit Text</h3>
                <button onClick={() => setEditingText(null)} data-id="element-1808">
                  <XIcon size={20} data-id="element-1809" />
                </button>
              </div>
              {editingText.field === 'bulletPoints' ? <textarea value={editingText.text} onChange={handleTextChange} className="w-full h-40 p-3 bg-[#141E2A] border border-[#F5F7FA] border-opacity-20 rounded text-[#F5F7FA] mb-4" placeholder="Enter bullet points (one per line)" data-id="element-1810" /> : <input type="text" value={editingText.text} onChange={handleTextChange} className="w-full p-3 bg-[#141E2A] border border-[#F5F7FA] border-opacity-20 rounded text-[#F5F7FA] mb-4" data-id="element-1811" />}
              <div className="flex justify-end space-x-3" data-id="element-1812">
                <button onClick={() => setEditingText(null)} className="px-4 py-2 border border-[#F5F7FA] border-opacity-20 rounded" data-id="element-1813">
                  Cancel
                </button>
                <button onClick={handleTextSave} className="px-4 py-2 bg-[#C8F169] text-[#1E2A38] rounded" data-id="element-1814">
                  Save
                </button>
              </div>
            </div>
          </div>}
        {/* Feature bullets for copy/paste into docs */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1815">
          <div className="p-6 rounded-xl border border-[#F5F7FA] border-opacity-10 bg-[#1A2331]" data-id="element-1816">
            <h2 className="font-semibold mb-4 text-[#C8F169]" data-id="element-1817">Key Features</h2>
            <ul className="list-disc ml-5 space-y-2" data-id="element-1818">
              <li data-id="element-1819">Calendar‑driven orchestration for upcoming meetings</li>
              <li data-id="element-1820">
                Per‑attendee inbox scans (recent threads, attachments,
                commitments)
              </li>
              <li data-id="element-1821">
                CRM enrichment (pipeline, case load, SLAs, ARR, stage deltas)
              </li>
              <li data-id="element-1822">Knowledge retrieval from files and prior briefs (RAG)</li>
              <li data-id="element-1823">Context graph and reasoning with company playbooks</li>
              <li data-id="element-1824">
                Persona‑based briefs (Sales, CS, Exec) with templates &
                guardrails
              </li>
              <li data-id="element-1825">Delivery to Slack, Inbox, Microsoft Teams</li>
              <li data-id="element-1826">Feedback loop with analytics and auto‑learning</li>
              <li data-id="element-1827">Privacy, security, consent, and audit logging baked‑in</li>
            </ul>
          </div>
          <div className="p-6 rounded-xl border border-[#F5F7FA] border-opacity-10 bg-[#1A2331]" data-id="element-1828">
            <h2 className="font-semibold mb-4 text-[#C8F169]" data-id="element-1829">
              Flow Highlights
            </h2>
            <ul className="list-disc ml-5 space-y-2" data-id="element-1830">
              <li data-id="element-1831">
                Attendee & entity resolution maps emails to CRM
                contacts/accounts
              </li>
              <li data-id="element-1832">Normalization stitches threads and deduplicates signals</li>
              <li data-id="element-1833">Reasoning blends LLM output with deterministic rules</li>
              <li data-id="element-1834">Action planner proposes next steps and tasks</li>
              <li data-id="element-1835">Localization adjusts language, KPIs, and tone by persona</li>
            </ul>
          </div>
        </div>
      </main>
    </div>;
}